import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Save, Eye, EyeOff } from 'lucide-react';
import { Dropzone } from './Dropzone';

const defaultData = {
  id: 'room_booking',
  title: 'Meeting / Training Room Rental Rates',
  image: '',
  specification: '560 sqf',
  weekday_half_day: 'RM 700',
  weekday_full_day: 'RM 1,400',
  weekday_additional: 'RM 200',
  weekend_half_day: 'RM 800',
  weekend_full_day: 'RM 1,600',
  weekend_additional: 'RM 200',
  capacity_theatre: '50 pax',
  capacity_classroom: '44 pax',
  capacity_ushape: '21 pax',
  capacity_cluster: '34 pax',
  inclusions: ['Projector / mic system', 'Tables and chairs', 'Whiteboard', 'Registration table', 'Pantry access'],
  facilities: ['Event space setup based on approved layout', 'Air-conditioning within booking window policy', 'WiFi', 'Basic venue support'],
};

const linesToList = (text) => text.split('\n').map((s) => s.trim()).filter(Boolean);

const RoomBookingPreview = ({ d }) => (
  <Card className="border-[#D4AF37]/30">
    <CardHeader><CardTitle className="text-base">Live Preview</CardTitle></CardHeader>
    <CardContent className="space-y-4 text-sm">
      {d.image && <img src={d.image} alt="" className="w-full h-40 object-cover rounded" />}
      <h3 className="text-lg font-semibold text-[#0A1628]">{d.title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2">Day Type</th>
              <th className="text-left py-2">Half Day</th>
              <th className="text-left py-2">Full Day</th>
              <th className="text-left py-2">Additional</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-100">
              <td className="py-2">Weekday</td>
              <td className="py-2 text-[#D4AF37] font-semibold">{d.weekday_half_day}</td>
              <td className="py-2 text-[#D4AF37] font-semibold">{d.weekday_full_day}</td>
              <td className="py-2 text-[#D4AF37] font-semibold">{d.weekday_additional}</td>
            </tr>
            <tr>
              <td className="py-2">Weekend</td>
              <td className="py-2 text-[#D4AF37] font-semibold">{d.weekend_half_day}</td>
              <td className="py-2 text-[#D4AF37] font-semibold">{d.weekend_full_day}</td>
              <td className="py-2 text-[#D4AF37] font-semibold">{d.weekend_additional}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div>
        <h4 className="font-semibold text-[#0A1628] mb-1">Room — {d.specification}</h4>
        <div className="grid grid-cols-2 gap-1 text-xs">
          <div>Theatre: <span className="font-medium">{d.capacity_theatre}</span></div>
          <div>Classroom: <span className="font-medium">{d.capacity_classroom}</span></div>
          <div>U-Shape: <span className="font-medium">{d.capacity_ushape}</span></div>
          <div>Cluster: <span className="font-medium">{d.capacity_cluster}</span></div>
        </div>
      </div>
      {d.inclusions?.length > 0 && (
        <div>
          <h5 className="font-semibold text-[#0A1628] mb-1">Inclusions</h5>
          <ul className="text-xs text-gray-600 space-y-0.5">
            {d.inclusions.map((it, i) => <li key={i}>✓ {it}</li>)}
          </ul>
        </div>
      )}
      {d.facilities?.length > 0 && (
        <div>
          <h5 className="font-semibold text-[#0A1628] mb-1">Facilities</h5>
          <ul className="text-xs text-gray-600 space-y-0.5">
            {d.facilities.map((it, i) => <li key={i}>✓ {it}</li>)}
          </ul>
        </div>
      )}
    </CardContent>
  </Card>
);

const RoomBookingManager = () => {
  const [data, setData] = useState(defaultData);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      const res = await adminApi.get('/room-booking');
      if (res.data && res.data.id) setData({ ...defaultData, ...res.data });
    })();
  }, []);

  const save = async () => {
    setLoading(true);
    try {
      await adminApi.post('/room-booking', data);
      toast({ title: 'Saved', description: 'Room booking updated' });
    } catch (e) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="room-booking-manager">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Room Booking</h2>
        <Button variant="outline" onClick={() => setShowPreview((s) => !s)} data-testid="room-preview-toggle">
          {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
          {showPreview ? 'Hide Preview' : 'Live Preview'}
        </Button>
      </div>

      <div className={`grid ${showPreview ? 'lg:grid-cols-2' : ''} gap-6 items-start`}>
      <Card>
        <CardHeader><CardTitle>Edit Content</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} />
          </div>
          <div>
            <Label>Room Image</Label>
            {data.image && <img src={data.image} className="w-64 h-40 object-cover rounded mb-2" alt="" />}
            <Dropzone compact onUpload={(url) => setData({ ...data, image: url })} label="Drop room image" />
          </div>
          <div>
            <Label>Specification</Label>
            <Input value={data.specification} onChange={(e) => setData({ ...data, specification: e.target.value })} />
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            <h3 className="md:col-span-3 font-semibold mt-2">Weekday Rates</h3>
            <div><Label>Half Day</Label><Input value={data.weekday_half_day} onChange={(e) => setData({ ...data, weekday_half_day: e.target.value })} /></div>
            <div><Label>Full Day</Label><Input value={data.weekday_full_day} onChange={(e) => setData({ ...data, weekday_full_day: e.target.value })} /></div>
            <div><Label>Additional</Label><Input value={data.weekday_additional} onChange={(e) => setData({ ...data, weekday_additional: e.target.value })} /></div>
            <h3 className="md:col-span-3 font-semibold mt-2">Weekend Rates</h3>
            <div><Label>Half Day</Label><Input value={data.weekend_half_day} onChange={(e) => setData({ ...data, weekend_half_day: e.target.value })} /></div>
            <div><Label>Full Day</Label><Input value={data.weekend_full_day} onChange={(e) => setData({ ...data, weekend_full_day: e.target.value })} /></div>
            <div><Label>Additional</Label><Input value={data.weekend_additional} onChange={(e) => setData({ ...data, weekend_additional: e.target.value })} /></div>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <h3 className="md:col-span-4 font-semibold mt-2">Capacity</h3>
            <div><Label>Theatre</Label><Input value={data.capacity_theatre} onChange={(e) => setData({ ...data, capacity_theatre: e.target.value })} /></div>
            <div><Label>Classroom</Label><Input value={data.capacity_classroom} onChange={(e) => setData({ ...data, capacity_classroom: e.target.value })} /></div>
            <div><Label>U-Shape</Label><Input value={data.capacity_ushape} onChange={(e) => setData({ ...data, capacity_ushape: e.target.value })} /></div>
            <div><Label>Cluster</Label><Input value={data.capacity_cluster} onChange={(e) => setData({ ...data, capacity_cluster: e.target.value })} /></div>
          </div>

          <div>
            <Label>Inclusions (one per line)</Label>
            <Textarea rows={4} value={data.inclusions.join('\n')} onChange={(e) => setData({ ...data, inclusions: linesToList(e.target.value) })} />
          </div>
          <div>
            <Label>Facilities (one per line)</Label>
            <Textarea rows={4} value={data.facilities.join('\n')} onChange={(e) => setData({ ...data, facilities: linesToList(e.target.value) })} />
          </div>

          <Button onClick={save} disabled={loading} className="bg-[#1565C0]" data-testid="save-room-booking-btn">
            <Save className="w-4 h-4 mr-2" />{loading ? 'Saving…' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>

      {showPreview && <RoomBookingPreview d={data} />}
      </div>
    </div>
  );
};

export default RoomBookingManager;
